// FacultyDashboard.js
import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useAuth } from "../context/AuthContext";
import * as XLSX from "xlsx"; // ✅ Excel export
import jsPDF from "jspdf"; // ✅ PDF export
import autoTable from "jspdf-autotable"; // ✅ Fix for PDF
import "./FacultyDashboard.css";

export default function FacultyDashboard() {
  const { facultyUser, logoutFaculty } = useAuth();
  const [timetable, setTimetable] = useState({});
  const [periods, setPeriods] = useState([]);
  const [courseMap, setCourseMap] = useState({});
  const [activeSession, setActiveSession] = useState(null);
  const [qrData, setQrData] = useState("");
  const [sessionCode, setSessionCode] = useState("");
  const [expiryTime, setExpiryTime] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState({});
  const [activeSection, setActiveSection] = useState("timetable");

  // NEW STATES
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [studentList, setStudentList] = useState([]);

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // ✅ Reload attendance
  const loadAttendanceRecords = () => {
    const allRecords = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith("attendance_")) {
        try {
          const record = JSON.parse(localStorage.getItem(key));
          if (record && record.subject) {
            const date = record.date;
            if (!allRecords[date]) allRecords[date] = [];
            allRecords[date].push(record);
          }
        } catch (e) {
          console.error("Error parsing attendance:", key, e);
        }
      }
    }
    setAttendanceRecords(allRecords);
  };

  useEffect(() => {
    if (!facultyUser) return;

    let filteredTimetable = {};
    let map = {};
    let allPeriods = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (key.endsWith("_timetable")) {
        try {
          const courseTT = JSON.parse(localStorage.getItem(key)) || {};
          const courseKey = key.replace("_timetable", "");

          const savedPeriods =
            JSON.parse(localStorage.getItem(`${courseKey}_periods`)) || [];
          if (savedPeriods.length > 0) {
            allPeriods = [...allPeriods, ...savedPeriods];
          }

          map[courseKey] = courseKey;

          Object.keys(courseTT).forEach((day) => {
            Object.keys(courseTT[day] || {}).forEach((periodLabel) => {
              const cell = courseTT[day][periodLabel];
              if (cell && cell.facultyId === facultyUser.id) {
                if (!filteredTimetable[day]) filteredTimetable[day] = {};
                filteredTimetable[day][periodLabel] = {
                  ...cell,
                  courseKey,
                };
              }
            });
          });
        } catch (err) {
          console.error("Error parsing timetable:", key, err);
        }
      }
    }

    const uniquePeriods = [];
    const seen = new Set();
    allPeriods.forEach((p) => {
      if (!seen.has(p.label)) {
        seen.add(p.label);
        uniquePeriods.push(p);
      }
    });

    setPeriods(uniquePeriods);
    setTimetable(filteredTimetable);
    setCourseMap(map);

    loadAttendanceRecords();
  }, [facultyUser]);

  // ✅ QR session
  const startQrSession = (cell, day, period) => {
    const sessionId = `${facultyUser.id}_${day}_${period}_${Date.now()}`;

    const sessionPayload = {
      sessionId,
      subject: cell.subject,
      facultyId: facultyUser.id,
      courseKey: cell.courseKey,
      dept: cell.deptName || "Unknown",
      year: cell.year || "N/A",
      semester: cell.semester || "N/A",
      batch: cell.batch || "N/A",
      day,
      period,
    };

    setActiveSession(sessionPayload);
    generateQrAndCode(sessionPayload);

    localStorage.setItem(
      `attendance_${sessionId}`,
      JSON.stringify({
        subject: cell.subject,
        dept: cell.deptName,
        year: cell.year,
        semester: cell.semester,
        batch: cell.batch,
        date: new Date().toLocaleDateString(),
        present: [],
      })
    );

    const interval = setInterval(() => {
      generateQrAndCode(sessionPayload);
      loadAttendanceRecords();
    }, 10000);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      setActiveSession(null);
      setQrData("");
      setSessionCode("");
      setExpiryTime(null);
    }, 5 * 60 * 1000);

    setExpiryTime(Date.now() + 5 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  };

  const generateQrAndCode = (sessionPayload) => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    const payload = {
      ...sessionPayload,
      sessionId: sessionPayload.sessionId,
      code,
      timestamp: Date.now(),
    };
    setQrData(JSON.stringify(payload));
    setSessionCode(sessionPayload.sessionId);
  };

  // ✅ Export Excel
  const exportToExcel = () => {
    if (!selectedCourse || studentList.length === 0) return;
    const sessions = Object.values(attendanceRecords).flat().filter(
      (rec) =>
        rec.subject === selectedCourse.subject &&
        rec.dept === selectedCourse.deptName &&
        rec.year === selectedCourse.year &&
        rec.semester === selectedCourse.semester &&
        rec.batch === selectedCourse.batch
    );

    const dates = [...new Set(sessions.map((s) => s.date))];

    const rows = studentList.map((stu) => {
      let presentCount = 0;
      const row = { "Student ID": stu.id, Name: stu.name };

      dates.forEach((d) => {
        const sessionsForDate = sessions.filter((s) => s.date === d);
        const isPresent = sessionsForDate.some((s) =>
          s.present.includes(stu.id)
        );
        row[d] = isPresent ? "P" : "AB";
        if (isPresent) presentCount++;
      });

      row["Total %"] =
        dates.length > 0
          ? Math.round((presentCount / dates.length) * 100) + "%"
          : "0%";

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
    XLSX.writeFile(workbook, `Attendance_${selectedCourse.subject}.xlsx`);
  };

  // ✅ Export PDF
  const exportToPDF = () => {
    if (!selectedCourse || studentList.length === 0) return;

    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(
      `Attendance Report - ${selectedCourse.subject} (${selectedCourse.deptName} | Y${selectedCourse.year} | S${selectedCourse.semester} | B${selectedCourse.batch})`,
      14,
      16
    );

    const sessions = Object.values(attendanceRecords).flat().filter(
      (rec) =>
        rec.subject === selectedCourse.subject &&
        rec.dept === selectedCourse.deptName &&
        rec.year === selectedCourse.year &&
        rec.semester === selectedCourse.semester &&
        rec.batch === selectedCourse.batch
    );

    const dates = [...new Set(sessions.map((s) => s.date))];

    const tableHead = ["Student ID", "Name", "Total %", ...dates];
    const tableBody = studentList.map((stu) => {
      let presentCount = 0;
      const marks = [];

      dates.forEach((d) => {
        const sessionsForDate = sessions.filter((s) => s.date === d);
        const isPresent = sessionsForDate.some((s) =>
          s.present.includes(stu.id)
        );
        if (isPresent) {
          marks.push("P");
          presentCount++;
        } else {
          marks.push("AB");
        }
      });

      const percentage =
        dates.length > 0
          ? Math.round((presentCount / dates.length) * 100)
          : 0;

      return [stu.id, stu.name, `${percentage}%`, ...marks];
    });

    autoTable(doc, {
      head: [tableHead],
      body: tableBody,
      startY: 24,
      theme: "grid",
      styles: { fontSize: 10, halign: "center" },
      headStyles: { fillColor: [75, 0, 130] },
    });

    doc.save(`Attendance_${selectedCourse.subject}.pdf`);
  };

  // ✅ Load student list
  useEffect(() => {
    if (selectedCourse) {
      const allStudents = JSON.parse(localStorage.getItem("students")) || [];
      const students = allStudents.filter((s) => {
        const studentDept = (s.departmentName || "").toLowerCase();
        const courseDept = (selectedCourse.deptName || "").toLowerCase();
        const studentSem = s.course?.sem || s.course?.semester;
        const courseSem = selectedCourse.semester || selectedCourse.sem;

        return (
          studentDept === courseDept &&
          s.course?.year === selectedCourse.year &&
          studentSem === courseSem &&
          s.course?.batch === selectedCourse.batch
        );
      });

      setStudentList(students);
    }
  }, [selectedCourse]);

  if (!facultyUser) {
    return <p>❌ Unauthorized. Please login first.</p>;
  }

  return (
    <div className="dashboard faculty">
      <h1>Faculty Dashboard</h1>
      <p>
        Welcome, {facultyUser?.name || "Faculty"} 👋 (ID: {facultyUser?.id})
      </p>

      <div className="faculty-layout">
        <ul className="faculty-menu">
          <li onClick={() => setActiveSection("timetable")}>Mark Attendance</li>
          <li
            onClick={() => {
              setActiveSection("attendance");
              setSelectedCourse(null);
            }}
          >
            View Student Records
          </li>
          <button className="logout-btn" onClick={logoutFaculty}>
            Logout
          </button>
        </ul>

        <div className="faculty-content">
          {/* 📌 Timetable */}
          {activeSection === "timetable" && (
            <div className="timetable-card">
              <h2>Your Timetable</h2>
              {Object.keys(timetable).length === 0 ? (
                <p>No timetable assigned yet.</p>
              ) : (
                <>
                  <table>
                    <thead>
                      <tr>
                        <th>Day</th>
                        {periods.map((p, i) => (
                          <th key={i}>
                            {p.label} <br />({p.time})
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {days.map((day) => (
                        <tr key={day}>
                          <td>{day}</td>
                          {periods.map((p, i) => {
                            const cell = timetable[day]?.[p.label];
                            return (
                              <td
                                key={i}
                                className={cell ? "clickable-cell" : ""}
                                onClick={() =>
                                  cell && startQrSession(cell, day, p.label)
                                }
                              >
                                {cell ? (
                                  <>
                                    <strong>{cell.subject}</strong>
                                    <br />
                                    <small>
                                      {cell.deptName || "Dept"} | Y{cell.year} | S
                                      {cell.semester} | B{cell.batch}
                                    </small>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}

          {/* 📌 Attendance Records */}
          {activeSection === "attendance" && (
            <div className="attendance-card">
              <h2>Student Records</h2>

              {!selectedCourse && (
                <>
                  {Object.keys(timetable).length === 0 ? (
                    <p>No courses assigned.</p>
                  ) : (
                    <ul>
                      {days.map((day) =>
                        periods.map((p) => {
                          const cell = timetable[day]?.[p.label];
                          return (
                            cell && (
                              <li
                                key={`${day}_${p.label}`}
                                className="clickable-cell"
                                onClick={() => setSelectedCourse(cell)}
                              >
                                {cell.subject} ({cell.deptName} | Y{cell.year} | S
                                {cell.semester} | B{cell.batch})
                              </li>
                            )
                          );
                        })
                      )}
                    </ul>
                  )}
                </>
              )}

              {selectedCourse && (
                <div>
                  <button onClick={() => setSelectedCourse(null)}>⬅ Back</button>
                  <h3>
                    {selectedCourse.subject} ({selectedCourse.deptName} | Y
                    {selectedCourse.year} | S{selectedCourse.semester} | B
                    {selectedCourse.batch})
                  </h3>

                  {studentList.length === 0 ? (
                    <p>No students enrolled.</p>
                  ) : (() => {
                      const sessions = Object.values(attendanceRecords).flat().filter(
                        (rec) =>
                          rec.subject === selectedCourse.subject &&
                          rec.dept === selectedCourse.deptName &&
                          rec.year === selectedCourse.year &&
                          rec.semester === selectedCourse.semester &&
                          rec.batch === selectedCourse.batch
                      );

                      const dates = [...new Set(sessions.map((s) => s.date))];

                      return (
                        <>
                          <table border="1" cellPadding="6">
                            <thead>
                              <tr>
                                <th>Student ID</th>
                                <th>Name</th>
                                <th>Total %</th>
                                {dates.map((d, i) => (
                                  <th key={i}>{d}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {studentList.map((stu) => {
                                let presentCount = 0;
                                const marks = {};

                                dates.forEach((d) => {
                                  const sessionsForDate = sessions.filter((s) => s.date === d);
                                  const isPresent = sessionsForDate.some((s) =>
                                    s.present.includes(stu.id)
                                  );

                                  if (isPresent) {
                                    marks[d] = "P";
                                    presentCount++;
                                  } else {
                                    marks[d] = "AB";
                                  }
                                });

                                const percentage =
                                  dates.length > 0
                                    ? Math.round((presentCount / dates.length) * 100)
                                    : 0;

                                return (
                                  <tr key={stu.id}>
                                    <td>{stu.id}</td>
                                    <td>{stu.name}</td>
                                    <td>{percentage}%</td>
                                    {dates.map((d, i) => (
                                      <td key={i}>{marks[d]}</td>
                                    ))}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>

                          {/* ✅ Export buttons moved below table */}
                          <div style={{ marginTop: "12px" }}>
                            <button onClick={exportToExcel} className="export-btn">
                              📊 Export Excel
                            </button>
                            <button onClick={exportToPDF} className="export-btn pdf">
                              📄 Export PDF
                            </button>
                          </div>
                        </>
                      );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ✅ QR Modal */}
      {activeSession && (
        <div className="qr-modal-overlay">
          <div className="qr-modal">
            <button
              className="qr-close-btn"
              onClick={() => setActiveSession(null)}
            >
              ✖
            </button>
            <h3>Attendance QR Code</h3>
            <QRCodeCanvas value={qrData} size={200} />
            <p>
              Session Code (ID): <strong>{sessionCode}</strong>
            </p>
            <p>
              Subject: <strong>{activeSession.subject}</strong>
              <br />
              Dept: {activeSession.dept} | Year {activeSession.year} | Sem{" "}
              {activeSession.semester} | Batch {activeSession.batch}
            </p>
            <p>
              Expires at:{" "}
              {new Date(expiryTime).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
